import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { CustomerPortalUser } from '@/types/customer-auth.types';

/**
 * Customer Module Phase 3 — isolated auth store for the Customer Portal
 * (D-050). Deliberately its own persisted key ('customer-auth') and its own
 * Zustand store instance — never shares state, storage, or a token with
 * `useAuthStore` (tenant staff) or `usePlatformAuthStore` (Platform Owner).
 */
interface CustomerAuthState {
  user: CustomerPortalUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isInitialized: boolean;
  setAuth: (user: CustomerPortalUser, accessToken: string, refreshToken: string) => void;
  setInitialUser: (user: CustomerPortalUser) => void;
  /** User Profile P2 — patch display-safe fields of the signed-in user (names, mustChangePassword…). */
  updateUser: (patch: Partial<CustomerPortalUser>) => void;
  rotateTokens: (accessToken: string, refreshToken: string) => void;
  clearAuth: () => void;
  setInitialized: () => void;
}

export const useCustomerAuthStore = create<CustomerAuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isInitialized: false,

      setAuth: (user, accessToken, refreshToken) =>
        set({ user, accessToken, refreshToken, isAuthenticated: true }),

      setInitialUser: (user) => set({ user, isAuthenticated: true }),

      updateUser: (patch) => set((s) => (s.user ? { user: { ...s.user, ...patch } } : {})),

      rotateTokens: (accessToken, refreshToken) => set({ accessToken, refreshToken }),

      clearAuth: () =>
        set({ user: null, accessToken: null, refreshToken: null, isAuthenticated: false }),

      setInitialized: () => set({ isInitialized: true }),
    }),
    {
      name: 'customer-auth',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);
